import { useCallback, useEffect, useRef, useState } from 'react';
import { useWorkspaceContext } from 'src/providers/WorkspaceProvider/Workspace.context';
import { TemplateDraftsService } from 'src/pages/WhatsappApi/services/TemplateDrafts.service';
import type { tTemplateDraft } from 'src/pages/WhatsappApi/types/TemplateDraft.types';

export type tTemplateDraftsState = {
  /** The workspace's drafts not yet submitted, most recently edited first. */
  templateDrafts: tTemplateDraft[];
  /** True once THIS workspace's list answered (or the feature is off). */
  templateDraftsLoaded: boolean;
  refreshTemplateDrafts: () => Promise<void>;
  /** The draft the wizard is editing; null when creating or editing a template. */
  draftToEdit: tTemplateDraft | null;
  setDraftToEdit: (draft: tTemplateDraft | null) => void;
};

/**
 * Template drafts (INT-1494), shared by the templates table, the wizard and the
 * post-connection notice. Lives in the module provider next to `templateToEdit`
 * for the same reason: switching windows unmounts the views.
 *
 * Fetched for every workspace (released in INT-1493). A failed read leaves
 * the previous list in place and marks the answer as received, so the table
 * does not wait forever on a service that is down.
 */
export function useTemplateDraftsState(): tTemplateDraftsState {
  const { currentWorkspaceId } = useWorkspaceContext();

  const [templateDrafts, setTemplateDrafts] = useState<tTemplateDraft[]>([]);
  const [loadedFor, setLoadedFor] = useState<string | null>(null);
  const [draftToEdit, setDraftToEdit] = useState<tTemplateDraft | null>(null);
  // The workspace the user is on now. An answer for any other one arrived
  // after a switch and is dropped: it would otherwise overwrite this
  // workspace's list with the previous one's drafts.
  const currentWorkspaceRef = useRef(currentWorkspaceId);
  currentWorkspaceRef.current = currentWorkspaceId;

  const refreshTemplateDrafts = useCallback(async () => {
    if (!currentWorkspaceId) return;
    const workspaceId = currentWorkspaceId;
    try {
      const response = await TemplateDraftsService.list(workspaceId);
      if (currentWorkspaceRef.current !== workspaceId) return;
      setTemplateDrafts(response?.drafts ?? []);
    } catch (error) {
      console.error('Failed to fetch template drafts:', error);
    } finally {
      if (currentWorkspaceRef.current === workspaceId) setLoadedFor(workspaceId);
    }
  }, [currentWorkspaceId]);

  // A new workspace is a new list: reset before fetching, or the previous
  // workspace's drafts would render under this one until the answer lands.
  useEffect(() => {
    setTemplateDrafts([]);
    setLoadedFor(null);
    setDraftToEdit(null);
    refreshTemplateDrafts();
  }, [refreshTemplateDrafts]);

  return {
    templateDrafts,
    templateDraftsLoaded: loadedFor === currentWorkspaceId,
    refreshTemplateDrafts,
    draftToEdit,
    setDraftToEdit,
  };
}
